import { Model } from '../../../api/model';
import { pluckPath } from '../../../support/helpers/pluck-path.function';
import { FieldMeta } from '../../../entities/models/field-meta.model';

export class FormModel extends Model {

    public name;
    public default: boolean   = false;
    public groups: Array<any> = [];

    protected increment = 0;

    public constructor(data: Object = {}) {
        super();
        this.boot(data);
    }

    // public groups() {
    //     return this.hasMany(FormGroupModel, 'groups');
    // }
    /**
     * Get a group by name
     *
     * @param groupName
     * @returns {FormGroupInterface[]}
     */
    public getGroup(id: number) {
        return this.groups.find(group => group.id === id);
    }

    /**
     * Add a new group
     *
     * @param groupName
     */
    public addGroup(groupName: string) {
        const group = {
            id: this.incremental(),
            name: groupName,
            order: this.getLastOrder(this, 'groups'),
            sections: []
        };

        this.groups.push(group);

        return group;
    }

    /**
     * Update a group
     *
     * @param updatedGroup
     * @returns {any}
     */
    public updateGroup(updatedGroup: FormGroupInterface) {
        let group = this.getField(updatedGroup.id);
        group     = Object.assign(group, updatedGroup);

        return group;
    }

    /**
     * Delete a group item by the given name
     *
     * @param groupName
     */
    public deleteGroup(id: number) {

        let deletedElements = [];

        this.groups = this.groups.filter(group => {
            if (group.id === id) {
                deletedElements = pluckPath(group.sections, 'rows.elements', true);
                return false;
            }

            return true;
        });

        return deletedElements;
    }

    /**
     * Get the section by id
     *
     * @param id
     * @returns {any}
     */
    public getSection(id: number) {
        let resultSection: any = false;

        this.groups.forEach(group => {
            group.sections.forEach(section => {
                if (section.id === id) {
                    resultSection = section;
                }
            });
        });

        return resultSection;
    }

    /**
     * Add a new section to a group
     *
     * @param groupID
     * @param sectionName
     * @returns {FormSectionInterface}
     */
    public addSection(groupID: number, sectionName: string) {
        const group = this.getGroup(groupID);

        const section: FormSectionInterface = {
            id: this.incremental(),
            name: sectionName,
            order: this.getLastOrder(group, 'sections'),
            rows: []
        };

        group.sections = group.sections.concat([section]);

        return section;
    }

    /**
     * Update section
     *
     * @param updatedSection
     * @returns {any}
     */
    updateSection(updatedSection: FormSectionInterface) {
        let section = this.getSection(updatedSection.id);
        section     = Object.assign(section, updatedSection);

        return section;
    }

    /**
     * Delete a section by its id
     *
     * @param id
     */
    public deleteSection(id: number) {

        let deletedElements = [];

        this.groups.forEach(group => {
            group.sections = group.sections.filter(section => {
                if (section.id === id) {
                    deletedElements = pluckPath(section.rows, 'elements', true);
                    return false;
                }

                return true;
            });
        });

        return deletedElements;
    }

    /**
     * Returns the row by the given id
     *
     * @param id
     * @returns {any}
     */
    public getRow(id: number) {
        let resultRow: any = false;

        this.groups.forEach(group => {
            group.sections.forEach(section => {
                section.rows.forEach(row => {
                    if (row.id === id) {
                        resultRow = row;
                    }
                });
            });
        });

        return resultRow;
    }

    /**
     * Return the row space that is left
     *
     * @param id
     * @returns {number}
     */
    public getRowSpace(id: number, exceptFieldUUID: number = null, applyNewSpace = false) {
        let space = 12;
        const row = this.getRow(id);

        if (!row) {
            return 0;
        }

        row.elements.forEach((element) => {
            if (exceptFieldUUID === null || exceptFieldUUID !== element.uuid) {
                space -= element.space;
            }
        });

        if (applyNewSpace && space > 0 && typeof exceptFieldUUID === 'string') {
            const field = this.getField(exceptFieldUUID);
            field.space = space;
        }

        return space;
    }

    /**
     * Add a new row
     *
     * @param sectionID
     * @param order
     * @returns {{id: number, order: (number|any), elements: Array}}
     */
    public addRow(sectionID: number, order?: number) {
        // get section
        const section = this.getSection(sectionID);

        // setup new field
        const newRow = {
            id: this.incremental(),
            order: order || this.getLastOrder(section, 'rows'),
            elements: []
        };

        // push the field in an immutable way
        section.rows = section.rows.concat([newRow]);

        return newRow;
    }

    /**
     * Delete a row by id
     *
     * @param id
     */
    public deleteRow(id: number) {

        let deletedElements = [];

        this.groups.forEach(group => {
            group.sections.forEach(section => {
                section.rows = section.rows.filter(row => {
                    if (row.id === id) {
                        deletedElements = row.elements;
                        return false;
                    }

                    return true;
                });
            });
        });

        return deletedElements;
    }

    /**
     * Returns a field by its id
     *
     * @param id
     * @returns {any}
     */
    public getField(uuid: string) {
        let resultField: any = false;

        this.groups.forEach(group => {
            group.sections.forEach(section => {
                section.rows.forEach(row => {
                    row.elements.forEach(field => {
                        if (field.uuid === uuid) {
                            resultField = field;
                        }
                    });
                });
            });
        });

        return resultField;
    }

    public getFieldRow(uuid: string) {
        let resultRow: any = false;

        this.groups.forEach(group => {
            group.sections.forEach(section => {
                section.rows.forEach(row => {
                    row.elements.forEach(field => {
                        if (field.uuid === uuid) {
                            resultRow = row;
                        }
                    });
                });
            });
        });

        return resultRow;
    }

    /**
     * Add a new field to a section
     *
     * @param sectionID
     * @param fieldType
     * @returns {{id: number, type: string, name: string, label: string, information: string, default_value: string, placeholder: string, validation: {}, options: {}}}
     */
    public addField(rowID: number, field: FieldMeta, index?: number) {
        // get row
        const row      = this.getRow(rowID);
        const rowSpace = this.getRowSpace(rowID);

        const newField = {
            id: field.id,
            field_meta_id: field['field_meta_id'],
            uuid: field.uuid,
            name: field.name,
            dynamic: field.dynamic,
            label: field.label,
            type: field.type,
            space: rowSpace
        };

        if (index >= 0) {
            // insert at specific index
            row.elements.splice(index, 0, newField);
        } else {
            // insert at the end of array
            row.elements = row.elements.concat([newField]);
        }

        // setup new field
        // let newField = {
        //     id: this.incremental(),
        //     order: this.getLastOrder(row, 'elements'),
        //     type: fieldType,
        //     name: '',
        //     label: '',
        //     space: this.getRowSpace(rowID),
        //     information: '',
        //     default_value: '',
        //     placeholder: '',
        //     validation: {},
        //     options: {},
        //     attributes: {}
        // };
        //
        // // mixin options
        // newField     = Object.assign(newField, options);
        // // push the field in an immutable way
        // row.elements = row.elements.concat([newField]);
        // // find elements in same row that must have their order incremented
        // this.incrementSubsequentElements(newField, row);

        return newField;
    }

    public moveField(rowID: number, fieldUUID: string, newIndex: number) {

        const field  = this.getField(fieldUUID);
        const newRow = this.getRow(rowID);
        let oldRow;

        // check if field is part of same row
        if (oldRow = this.getFieldRow(fieldUUID) == newRow) {
            // if yes -> update order
            console.warn('same row');

        } else { // if not -> remove from old row and add to new row
            // update order of new row
            console.warn('new row');
        }
    }

    /**
     * Move field to a given row
     *
     * @param fieldID
     * @param rowID
     */
    public moveFieldToRow(fieldUUID: string, rowID: number, order: number = 0) {
        const field = this.getField(fieldUUID);
        const row   = this.getRow(rowID);

        if (!row || !field) {
            return false;
        }

        this.deleteField(fieldUUID);
        // push field to row
        row.elements = row.elements.concat([field]);
        field.order  = order;

        this.incrementSubsequentElements(field, row);
    }

    /**
     * Update field
     *
     * @param updatedField
     * @returns {any}
     */
    public updateField(updatedField: FormElementInterface) {
        let field = this.getField(updatedField.uuid);
        field     = Object.assign(field, updatedField);

        return field;
    }

    /**
     * Delete a field by its id
     *
     * @param id
     */
    public deleteField(uuid: string) {
        this.groups.forEach(group => {
            group.sections.forEach(section => {
                section.rows.forEach(row => {
                    row.elements = row.elements.filter(element => element.uuid !== uuid);
                });
            });
        });
    }

    /**
     * Assign new order values to all objects
     */
    public reorder() {
        let index = 0;
        this.groups.forEach(group => {
            group.order = index;
            index++;

            let index2 = 0;
            group.sections.forEach(section => {
                section.order = index2;
                index2++;

                let index3 = 0;
                section.rows.forEach(row => {
                    row.order = index3;
                    index3++;

                    let index4 = 0;
                    row.elements.forEach(element => {
                        element.order = index4;
                        index4++;
                    });
                });
            });
        });
    }

    /**
     * Get last order + 1
     *
     * @param orderedArray
     * @param key
     * @returns {any}
     */
    public getLastOrder(parentObject, key) {
        // if length is 0 -> order is the initial value
        if (!parentObject[key].length) {
            return 0;
        }

        // get the last group
        const last = parentObject[key].reduce((prev, current) => {
            return prev.order < current.order ? current : prev;
        });

        return last.order + 1;
    }

    /**
     * Increment subsequent elements
     *
     * @param originElement
     * @param row
     */
    private incrementSubsequentElements(originElement: FormElementInterface, row: FormRowInterface) {

        let newOrder = 0;
        row.elements = row.elements.map(element => {

            if (element.order < originElement.order || element.order > originElement.order) {

                element.order = newOrder;

            } else if (element.order === originElement.order && element.id !== originElement.id) {

                if (newOrder >= element.order) {
                    newOrder++;
                }
                element.order = newOrder;

            } else if (element.id === originElement.id) {

                return element;
            }

            newOrder++;

            return element;
        });

        // re-sort it
        row.elements.sort((a, b) => {
            return a.order - b.order;
        });
    }

    private incremental() {
        return this.increment++;
    }

}

export interface FormInterface {
    groups: Array<FormGroupInterface>;
}

export interface FormGroupInterface {
    id: any;
    name: string;
    order: number;
    sections: Array<FormSectionInterface>;
}

export interface FormSectionInterface {
    id: number;
    name: string;
    order?: number;
    rows?: Array<FormRowInterface>;
}

export interface FormRowInterface {
    id: number;
    order: number;
    elements: Array<FormElementInterface>;
}

export interface FormElementInterface {
    id: number;
    uuid: string;
    order: number;
    type: string;
    name: string;
    label: string;
    space: number;
    information?: string;
    default_value?: any;
    placeholder?: string;
    validation?: Object;
    options?: Object;
    attributes?: Object;
}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/forms/form/models/form.model.ts